import { refreshNuxtData } from '#app/composables/asyncData'
import type {
  NotificationComponent,
  NotificationOnClickActions,
} from '#storefront-ui'
import { useNotification } from '#storefront-ui'
import type { RouteLocationRaw } from '#vue-router'
import { useNuxtApp } from '#app'

export type ToastAction = 'CONFIRM' | 'RELOAD' | 'ROUTE'
export type ToastType = 'INFO' | 'SUCCESS' | 'ERROR'
export type ToastOptions = {
  duration?: number
  action?: ToastAction
  type?: ToastType
  to?: RouteLocationRaw
  withProgress?: boolean
  position?:
    | 'top'
    | 'bottom'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
}

export function useToast() {
  const { $i18n } = useNuxtApp()
  const notification = useNotification()

  const getActions = (action: ToastAction, to?: RouteLocationRaw) => {
    const classes = 'font-normal text-transform-unset'
    const actions = {
      CONFIRM: [
        {
          class: classes,
          text: $i18n.t('notification.confirm'),
          onClick: (actions: NotificationOnClickActions) => actions.close(),
        },
      ],
      ROUTE: [
        {
          class: classes,
          text: $i18n.t('notification.view'),
          href: to,
        },
      ],
      RELOAD: [
        {
          class: classes,
          text: $i18n.t('notification.reload_page'),
          onClick: (actions: NotificationOnClickActions) => actions.close(),
        },
        {
          class: classes,
          text: 'X',
          onClick: () => Promise.resolve(refreshNuxtData()),
        },
      ],
    }

    return actions[action]
  }

  const getType = (
    toastType?: ToastType,
  ): NotificationComponent & {
    iconClass?: string
    variant?: ToastType
  } => {
    if (!toastType) {
      return {
        classes: 'text-white bg-black',
      }
    }

    const type = {
      //NOTE color update me be required after Figma
      SUCCESS: {
        classes: 'bg-green-100 text-anthracite-800',
        iconComponent: 'IconCheckGreen',
        iconClass: 'text-green-500',
        variant: toastType,
      },
      //NOTE color update me be required after Figma
      INFO: {
        classes: 'text-accent bg-status-info',
        iconComponent: 'IconInfo',
        iconClass: '',
        variant: toastType,
      },
      //NOTE color update me be required after Figma
      ERROR: {
        classes: 'text-white bg-status-error-light',
        iconComponent: 'IconError',
        iconClass: '',
        variant: toastType,
      },
    }

    return type[toastType]
  }

  const show = (message: string | string[], options: ToastOptions = {}) => {
    const { action, type, to, position } = options // Destructure the position
    const finalMessage = Array.isArray(message) ? message.join('\n') : message

    notification.show(finalMessage, {
      ...options,
      actions: action && getActions(action, to),
      type: getType(type),
      position,
    })
  }

  return {
    show,
  }
}
